// ORCA360 - Event Detection Hook
// Sprint 2, Step 5: Event Detection
// React hook for triggering event detection via Inngest and polling for completion

import { useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface FetchedEvent {
  id: string;
  severity: 'critical' | 'warning' | 'positive' | 'info';
  event_type: string;
  confidence: number | null;
}

interface EventDetectionResult {
  success: boolean;
  sessionId: string;
  transcriptId: string;
  eventsDetected: number;
  statistics: {
    total: number;
    critical: number;
    warning: number;
    positive: number;
    info: number;
    byType: Record<string, number>;
    avgConfidence: number;
  };
  events: FetchedEvent[];
  message: string;
}

interface EventDetectionOptions {
  useLLM?: boolean;
  aircraftType?: string;
  flightPhase?: string;
  scenario?: string;
}

interface UseEventDetectionReturn {
  detectEvents: (
    sessionId: string,
    transcriptId: string,
    options?: EventDetectionOptions
  ) => Promise<EventDetectionResult | null>;
  loading: boolean;
  error: string | null;
  result: EventDetectionResult | null;
  progress: number;
}

const POLL_INTERVAL_MS = 1500; // Poll every 1.5s for responsive progress
const MAX_POLL_ATTEMPTS = 240; // 240 * 1.5s = 6 minutes max

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Hook for triggering event detection on a transcript.
 * Dispatches to Inngest for background processing, then polls for completion.
 */
export function useEventDetection(): UseEventDetectionReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EventDetectionResult | null>(null);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const detectEvents = useCallback(async (
    sessionId: string,
    transcriptId: string,
    options?: EventDetectionOptions
  ): Promise<EventDetectionResult | null> => {
    // Cancel any ongoing poll from a previous run
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    setLoading(true);
    setError(null);
    setResult(null);
    setProgress(0);

    try {
      console.log(`[useEventDetection] Dispatching event detection for session ${sessionId}`);

      // Get auth token
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const authToken = authSession?.access_token;
      if (!authToken) throw new Error('Not authenticated. Please log in and try again.');

      // 1. Dispatch to Inngest via API (returns immediately with jobId)
      const response = await fetch('/api/detect-events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          sessionId,
          transcriptId,
          ...options,
        }),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start event detection');
      }

      const { jobId } = await response.json();
      console.log(`[useEventDetection] Job dispatched: ${jobId}, polling for completion...`);

      // 2. Poll processing_jobs table for completion
      // Start with a short initial delay (Inngest needs a moment to pick up the event)
      await sleep(500);

      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        if (signal.aborted) throw new Error('Event detection cancelled');

        const { data: job, error: pollError } = await supabase
          .from('processing_jobs')
          .select('status, progress, error_message')
          .eq('id', jobId)
          .single();

        if (pollError) {
          console.warn('[useEventDetection] Poll error:', pollError);
          await sleep(POLL_INTERVAL_MS);
          continue;
        }

        if (job?.progress != null && job.progress > 0) {
          setProgress(job.progress);
        }

        if (job?.status === 'completed') {
          setProgress(100);
          console.log(`[useEventDetection] Job completed, fetching events...`);

          // Fetch the stored events from the database
          const { data: events, error: eventsError } = await supabase
            .from('events')
            .select('id, severity, event_type, confidence')
            .eq('session_id', sessionId)
            .eq('dismissed', false);

          if (eventsError) {
            console.error('[useEventDetection] Error fetching events:', eventsError);
          }

          const eventsList = (events || []) as FetchedEvent[];
          const byType: Record<string, number> = {};
          let totalConfidence = 0;
          eventsList.forEach((e) => {
            byType[e.event_type] = (byType[e.event_type] || 0) + 1;
            totalConfidence += e.confidence || 0;
          });

          const detectionResult: EventDetectionResult = {
            success: true,
            sessionId,
            transcriptId,
            eventsDetected: eventsList.length,
            statistics: {
              total: eventsList.length,
              critical: eventsList.filter((e) => e.severity === 'critical').length,
              warning: eventsList.filter((e) => e.severity === 'warning').length,
              positive: eventsList.filter((e) => e.severity === 'positive').length,
              info: eventsList.filter((e) => e.severity === 'info').length,
              byType,
              avgConfidence: eventsList.length > 0 ? totalConfidence / eventsList.length : 0,
            },
            events: eventsList,
            message: 'Event detection completed successfully',
          };

          setResult(detectionResult);
          setProgress(100);
          setLoading(false);
          return detectionResult;
        }

        if (job?.status === 'failed') {
          throw new Error(job.error_message || 'Event detection failed');
        }

        // Wait before next poll
        await sleep(POLL_INTERVAL_MS);
      }

      throw new Error('Event detection timed out. The job may still be running in the background.');
    } catch (err) {
      if (signal.aborted) {
        // Silently ignore aborted requests (component unmounted or new run started)
        return null;
      }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('[useEventDetection] Error:', errorMessage);

      setError(errorMessage);
      setLoading(false);

      return null;
    }
  }, []);

  return {
    detectEvents,
    loading,
    error,
    result,
    progress,
  };
}
